/**
 * The notification-detail view: rendered when the page is opened with
 * `?notifid=<id>` (the canonical click destination — see notification-click.ts
 * and PLAN.md). A focused single-notification state, not an inbox: full
 * title/body exactly as delivered, a category icon, and one gag.
 */
import { isValidNotificationId } from '../shared/ids';
import { gagText } from './gags';
import { t, type Locale } from './i18n';
import { esc } from './state';
import { takeDetail, type StoredDetail } from './detail-store';

export interface DetailLoadDeps {
  href: string;
  replaceState: (url: string) => void;
  caches: CacheStorage;
  now: number;
}

/**
 * Reads `notifid` off the URL, resolves the stored payload (if any), and
 * unconditionally cleans the query string via the History API — no reload,
 * no new navigation. A missing `notifid` is a no-op: normal `/` behavior is
 * untouched. An invalid id or a missing/expired payload still cleans the URL
 * and returns `null`, so the caller falls back to the normal landing state
 * rather than an error page.
 */
export async function loadDetailFromUrl(deps: DetailLoadDeps): Promise<StoredDetail | null> {
  const url = new URL(deps.href);
  const id = url.searchParams.get('notifid');
  if (id === null) return null;

  const detail = isValidNotificationId(id) ? await takeDetail(deps.caches, id, deps.now) : null;

  url.searchParams.delete('notifid');
  try {
    deps.replaceState(url.pathname + url.search + url.hash);
  } catch {
    // URL cleanup is a UX nicety (see PLAN.md), never a reason for this to
    // fail — the detail (or lack of one) still resolves correctly either way.
  }
  return detail;
}

const ICON_PATTERN = /^\/icons\/[a-zA-Z0-9_-]+\.(?:png|svg)$/;

/** Only ever renders one of our own known-shape icon paths, whatever a payload claims. */
export function safeIcon(icon: string | undefined): string {
  return icon && ICON_PATTERN.test(icon) ? icon : '/icons/default.png';
}

export function detailHtml(detail: StoredDetail, gagIndex: number, locale: Locale): string {
  return `<div class="hero-inner detail-inner">
        <div class="detail-icon" aria-hidden="true"><img src="${esc(safeIcon(detail.i))}" alt="" width="32" height="32" /></div>
        <h1 class="detail-title">${esc(detail.t)}</h1>
        <p class="detail-body">${esc(detail.b)}</p>
        <hr class="detail-rule" />
        <p class="detail-gag">${esc(gagText(gagIndex, locale))}</p>
        <button type="button" class="btn btn-text" data-action="detail-close">${esc(t('detailBack', locale))}</button>
      </div>`;
}

export function renderDetail(container: HTMLElement, detail: StoredDetail, gagIndex: number, locale: Locale, onClose: () => void): void {
  container.innerHTML = detailHtml(detail, gagIndex, locale);
  container.querySelector<HTMLButtonElement>('[data-action="detail-close"]')?.addEventListener('click', onClose);
}
